/**
 * Session Engine v3 — canonical event setup.
 *
 * The entry screen: conditions-met "job to be done" cards (SPEC-03) that seed
 * an Event, plus a manual create path.
 *
 * Context mode (isolated / app_linked / cycle_linked) is CHOSEN at the start
 * (S-21, owner ruling 2026-10-14): one three-way choice, Cycle / App /
 * Stand-alone, pre-selected with the guess below and limited to the modes the
 * workflow allows. The guess, as before, is inferred from choices
 * made on this screen, NOT declared upfront — per SPEC-05 §8.
 *
 * If the facilitator selects a Cycle + Track → cycle_linked.
 * If the facilitator starts with no track → app_linked by default.
 * If they type items directly without connecting anything → isolated.
 *
 * Started from inside a cycle or track (owner ruling 2026-11-03), the session
 * is related to it without asking: the screen says which cycle and track it
 * belongs to, and the chooser opens only on "Change".
 */

import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronRight, Loader2, Sparkles, MapPin, CheckCircle2, Users, Package, SlidersHorizontal } from 'lucide-react';
import { GOAL_STEP_SCOPE, useSessionEngineStore } from '../store';
import { stepPrerequisitesFor } from '../engineLogic';
import { SESSION_JOBS, type SessionJob } from '../sessionJobs';
import { STEP_META, STEP_ORDER, type ContainerMode, type ContextMode, type StepId } from '../types';
import { ExchangeContextPanel } from '../../../components/prioritization/sessions/ExchangeContextPanel';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { SituationQuestions } from '../../../components/prioritization/sessions/SituationQuestions';
import { resolveSessionPrefill, resolveSessionArrivalFrame, prefillOriginLabel } from '../../../lib/sessionPrefill';
import { usePinnedCycleStore } from '../../../stores/prioritization/pinnedCycleStore';
import { METHOD_REGISTRY } from '../../../lib/methodRegistry';
import type { SituationVector } from '../../../types/prioritization/situationVector';
import { SessionModeChooser } from '../../../components/prioritization/sessions/SessionModeChooser';
import { preselectSessionMode } from '../../../lib/sessions/sessionMode';
import { resolveEffectiveWorkflowConfig } from '../../../lib/workflowConfig';
import { SESSION_CONTEXT_MODES, SESSION_CONTEXT_MODE_LABELS } from '../../../types/prioritization/workflow';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';

/**
 * What a goal actually requires, and what it merely offers.
 *
 * Required = the hard prerequisites from `stepPrerequisitesFor` (owner ruling
 * 2026-09-22): nothing for S1, S2 or S3; items + criteria for S4; items for S5.
 * Everything else the canonical matrix associates with the goal is an OFFER the
 * creator may skip — it is still set up, marked optional.
 *
 * Two requirements are stated as conditions rather than as steps, because what
 * triggers them is chosen later:
 *   • Participants (S1) — needed only to run a step collaboratively.
 *   • Criteria (S3) + values (S4) at S5 — needed only by tools that score
 *     against criteria (weighted scoring, RICE, WSJF, cost of delay).
 */
function requiredForGoal(step: StepId): StepId[] {
  return stepPrerequisitesFor(step, 'declared');
}

function optionalForGoal(step: StepId): StepId[] {
  const required = new Set(requiredForGoal(step));
  return GOAL_STEP_SCOPE[step].filter((s) => !required.has(s));
}

/** A requirement that depends on a choice made later, said in plain words. */
const GOAL_CONDITIONAL_NOTE: Partial<Record<StepId, string>> = {
  S5: 'Criteria and values are needed only if you pick a tool that scores against them — weighted scoring, RICE, WSJF, cost of delay. Stack ranking and pairwise do not need them.',
};

interface FrameStats {
  participantCount: number;
  itemCount: number;
  criteriaCount: number;
  loading: boolean;
}

export function EngagementSetup() {
  const createEvent = useSessionEngineStore((s) => s.createEvent);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // The SessionPrefill contract (SPEC-04): rich router state from a "Rank
  // these in a session" sender (whiteboard reveal / boards page), or the
  // exchange ids the Catchball Inbox puts in the URL. Captured once; a refresh
  // drops router state and degrades to the URL's exchange ids alone.
  const [prefill] = useState(() => resolveSessionPrefill(location.state, searchParams));
  const [title, setTitle] = useState(prefill?.ask ?? '');
  // Named the way the Sessions list named it (the method registry), so the
  // button the person clicked and this line say the same thing.
  const methodName = (toolId: string) =>
    METHOD_REGISTRY.find((m) => m.methodId === toolId || m.variants.some((v) => v.toolId === toolId))?.name ?? null;
  const prefillToolName = prefill?.toolId ? methodName(prefill.toolId) : null;
  const alsoRecommended = (prefill?.recommendedToolIds ?? [])
    .filter((t) => t !== prefill?.toolId)
    .map(methodName)
    .filter((n): n is string => n !== null);
  const [showJobs, setShowJobs] = useState(false);
  const [creating, setCreating] = useState(false);
  const [primaryStep, setPrimaryStep] = useState<StepId | null>(null);
  // The four situation questions. Optional; null = not declared = today's behaviour.
  const [situation, setSituation] = useState<SituationVector | null>(null);

  // S-21: the mode the facilitator chose (null = take the pre-selected guess).
  const [chosenMode, setChosenMode] = useState<ContextMode | null>(null);
  const [allowedModes, setAllowedModes] = useState<readonly ContextMode[]>(SESSION_CONTEXT_MODES);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [cycleId, setCycleId] = useState('');
  const [trackId, setTrackId] = useState('');
  const [tracks, setTracks] = useState<Array<{ id: string; name?: string | null }>>([]);
  const [frameStats, setFrameStats] = useState<FrameStats>({ participantCount: 0, itemCount: 0, criteriaCount: 0, loading: false });

  const cycleRows = useDegapProcessStore((s) => s.cycles);
  const cycles = cycleRows ?? [];
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);
  const getTracksForCycle = useTrackStore((s) => s.getTracksForCycle);

  const activeCycles = cycles.filter((c) => (c as { status?: string }).status !== 'archived');
  const cycleName = cycles.find((c) => c.id === cycleId)?.name;
  const trackName = tracks.find((t) => t.id === trackId)?.name;

  // The chosen mode when it is allowed and possible; otherwise the guess
  // (a cycle and track picked → Cycle, else App), moved to an allowed mode.
  const inferredContext: ContextMode =
    chosenMode && allowedModes.includes(chosenMode) && (chosenMode !== 'cycle_linked' || (cycleId && trackId))
      ? chosenMode
      : preselectSessionMode({ cycleId, trackId }, allowedModes);
  const scratchMode = inferredContext === 'isolated';
  const isFrameLinked = inferredContext === 'cycle_linked';
  // A Stand-alone session carries no cycle; only a Cycle session carries a
  // track (an App session chosen over a picked track leaves the track behind).
  const carriesCycle = !scratchMode;

  // The workflow's allowed session modes (the cycle's workflow, else the
  // workspace default) — the one resolver session presets use.
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    void resolveEffectiveWorkflowConfig({ workspaceId, cycleId: cycleId || null })
      .then((cfg) => { if (!cancelled) setAllowedModes(cfg.allowedSessionModes); })
      .catch(() => { if (!cancelled) setAllowedModes(SESSION_CONTEXT_MODES); });
    return () => { cancelled = true; };
  }, [workspaceId, cycleId]);

  const setScratchMode = (on: boolean) => setChosenMode(on ? 'isolated' : null);

  useEffect(() => {
    void fetchCycles?.();
  }, [fetchCycles]);

  // A track arriving in the URL cannot be applied yet: its <option> does not
  // exist until the cycle's tracks load. A controlled select element whose value
  // matches no option renders the placeholder AND the DOM resets its value to
  // '', after which React sees unchanged state and never re-applies it — so the
  // Catchball Inbox handed the Designer ?trackId=… and the picker sat empty. Hold it
  // until the option exists.
  const [pendingTrackId, setPendingTrackId] = useState<string | null>(null);

  // The cycle and track this session is started from inside: the link the
  // person clicked, else the cycle/track the app's focus is on. Captured once.
  const [arrival] = useState(() =>
    resolveSessionArrivalFrame(searchParams, prefill, usePinnedCycleStore.getState()),
  );
  // Applied once known to be real: a link's ids at once; focus only when the
  // cycle is one of this workspace's (focus is kept across workspace switches).
  const [arrivalApplied, setArrivalApplied] = useState(false);
  // "Change" opens the chooser; until then the relation is settled, not asked.
  const [changingFrame, setChangingFrame] = useState(false);
  const frameSettled = arrivalApplied && !changingFrame;

  useEffect(() => {
    if (!arrival || arrivalApplied) return;
    if (arrival.source === 'focus' && !(cycleRows ?? []).some((c) => c.id === arrival.cycleId)) return;
    setArrivalApplied(true);
    setScratchMode(false);
    setCycleId(arrival.cycleId);
    if (arrival.trackId) setPendingTrackId(arrival.trackId);
  }, [arrival, arrivalApplied, cycleRows]);

  useEffect(() => {
    if (!cycleId) { setTracks([]); setTrackId(''); return; }
    void fetchTracksForCycle(cycleId).then(() => {
      const list = getTracksForCycle(cycleId);
      setTracks(list);
      if (pendingTrackId && list.some((t) => t.id === pendingTrackId)) {
        setTrackId(pendingTrackId);
        setPendingTrackId(null);
      } else if (arrival && !arrival.trackId && arrival.cycleId === cycleId && list.length === 1) {
        // Started from inside a cycle with one track: that track is the one.
        setTrackId((current) => current || list[0].id);
      }
    });
  }, [cycleId, fetchTracksForCycle, getTracksForCycle, pendingTrackId, arrival]);

  // Fetch participant + item counts when a track is selected — so the user can
  // see exactly what will be inherited before they commit to creating the event.
  useEffect(() => {
    if (!trackId) { setFrameStats({ participantCount: 0, itemCount: 0, criteriaCount: 0, loading: false }); return; }
    setFrameStats(prev => ({ ...prev, loading: true }));
    void (async () => {
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        const [{ count: pCount }, { data: pool }, { count: cCount }] = await Promise.all([
          supabase.from('track_participants').select('*', { count: 'exact', head: true }).eq('track_id', trackId).eq('status', 'active'),
          supabase.from('item_pools').select('priority_ids').eq('track_id', trackId).limit(1).maybeSingle(),
          supabase.from('cycle_attribute_configs').select('*', { count: 'exact', head: true }).eq('track_id', trackId),
        ]);
        setFrameStats({
          participantCount: pCount ?? 0,
          itemCount: (pool as { priority_ids?: string[] } | null)?.priority_ids?.length ?? 0,
          criteriaCount: cCount ?? 0,
          loading: false,
        });
      } catch {
        setFrameStats(prev => ({ ...prev, loading: false }));
      }
    })();
  }, [trackId]);

  function buildOpts(defaultTitle: string) {
    // Exchange ids: the Catchball Inbox's query params win; a router-state prefill
    // (SessionPrefill contract) supplies them when the URL carries none.
    const loopId = searchParams.get('loopId') || prefill?.exchange?.loopId || undefined;
    const ballId = searchParams.get('ballId') || prefill?.exchange?.ballId || undefined;
    return {
      title: title.trim() || defaultTitle,
      contextMode: inferredContext,
      cycleId: carriesCycle ? cycleId || undefined : undefined,
      cycleName: carriesCycle ? cycleName ?? undefined : undefined,
      trackId: isFrameLinked ? trackId || undefined : undefined,
      trackName: isFrameLinked ? trackName ?? undefined : undefined,
      // Catchball "Configure/Run session" lands here with ?loopId=
      loopId: scratchMode ? undefined : loopId,
      catchballBallId: scratchMode ? undefined : ballId,
      situationVector: situation,
      // A tool and a context chosen before the Designer opened (Sessions list:
      // a recommended tool, or the configuration the session belongs to).
      toolId: prefill?.toolId,
      contextId: prefill?.contextId,
    };
  }

  /**
   * createEvent, then seed any prefilled platform items (SPEC-04: whiteboard
   * "Rank these in a session" senders). source:'platform' keeps the real item
   * ids, so the event ranks the committed rows — never typed copies.
   */
  async function createWithPrefill(opts: Parameters<typeof createEvent>[0]) {
    await createEvent(opts);
    if (prefill?.items?.length) {
      useSessionEngineStore.getState().addItems(
        prefill.items.map((it) => ({ id: it.id, title: it.title, source: 'platform' as const })),
      );
    }
  }

  async function startFromGoal(step: StepId) {
    if (creating) return;
    setCreating(true);
    try {
      await createWithPrefill({
        ...buildOpts(`${STEP_META[step].label} session`),
        primaryStep: step,
        containerMode: derivedContainerMode,
      });
    } finally {
      setCreating(false);
    }
  }

  async function startFromJob(job: SessionJob) {
    if (creating) return;
    setCreating(true);
    try {
      await createWithPrefill({
        ...buildOpts(job.title),
        jobId: job.id,
        containerMode: job.containerDefault,
      });
    } finally {
      setCreating(false);
    }
  }

  async function startCustom() {
    if (creating) return;
    setCreating(true);
    try {
      await createWithPrefill({
        ...buildOpts(title.trim() || 'Untitled event'),
        containerMode: derivedContainerMode,
      });
    } finally {
      setCreating(false);
    }
  }

  // When a goal step is selected, show what it needs and what it merely offers.
  const requiredSteps = primaryStep ? requiredForGoal(primaryStep) : [];
  const optionalSteps = primaryStep ? optionalForGoal(primaryStep) : [];
  // Steps that will be pre-populated from the track (S1 + S2 when cycle_linked)
  const inheritedSteps: StepId[] = isFrameLinked ? ['S1', 'S2'] : [];

  // The container is DERIVED, never asked (owner ruling 2026-08-31). An Event is
  // the parent of the S1–S5 sessions that produce one result, so how many
  // sittings it holds falls out of how many steps actually have to be run —
  // which is the goal step plus its prerequisites, minus whatever the Track
  // already supplies. One step to run is one sitting, and a container with a
  // single child is a layer nobody should have to click through.
  // Only the steps that genuinely have to be run count — an optional step the
  // creator skips should not force a multi-sitting container on them.
  const stepsToRun = primaryStep
    ? [primaryStep, ...requiredSteps].filter((step) => !inheritedSteps.includes(step))
    : [];
  const derivedContainerMode: ContainerMode = stepsToRun.length > 1 ? 'multi' : 'single';

  return (
    <div className="mx-auto max-w-4xl space-y-6 py-2">
      <header className="space-y-1">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-electric-mustard-100 dark:bg-electric-mustard-900/30 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-electric-mustard-800 dark:text-electric-mustard-300">
          <Sparkles size={12} /> New Event
        </span>
        <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Set up a prioritization event</h1>
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          {frameSettled
            ? 'Pick what you need from this event.'
            : 'Pick what you need from this event. A cycle and a track are optional — connect one only if you want it to fill in participants and items for you.'}
        </p>
        {prefill?.items && prefill.items.length > 0 && (
          <p className="inline-flex items-center gap-1.5 border border-alpine-green-200 dark:border-alpine-green-800 bg-alpine-green-50 dark:bg-alpine-green-900/20 px-3 py-1.5 text-xs text-alpine-green-800 dark:text-alpine-green-300">
            <Package size={12} />
            {prefillOriginLabel(prefill.origin)} — {prefill.items.length} item{prefill.items.length !== 1 ? 's' : ''} will be
            added when you create the event.
          </p>
        )}
        {prefillToolName && (
          <p className="inline-flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--surface)] px-3 py-1.5 text-xs text-[var(--text-sec)]">
            <SlidersHorizontal size={12} />
            The ranking will use {prefillToolName}
            {alsoRecommended.length > 0 ? ` (also recommended: ${alsoRecommended.join(', ')})` : ''}. You can change it in
            the ranking step.
          </p>
        )}
      </header>

      {/* ── Where the work comes from — optional ── */}
      <section className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Where the items come from{' '}
              {!frameSettled && <span className="font-normal text-[var(--text-ter)]">(optional)</span>}
            </h2>
            {!frameSettled && (
              <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-0.5">
                A track carries participants, item pools, criteria and governance from its cycle. Connect one and
                S1 and S2 are filled in for you. You do not have to: an event runs perfectly well with no cycle and
                no track — pick items from the workspace, or type them in.
              </p>
            )}
          </div>
        </div>

        {frameSettled ? (
          <div data-testid="session-arrival-frame" className="space-y-2">
            <div className="flex items-start justify-between gap-3 border border-[var(--rule)] bg-[var(--surface)] px-3 py-2.5">
              <p className="text-sm text-[var(--text-pri)]">
                <MapPin size={12} className="inline mr-1.5 text-[var(--text-ter)]" />
                {carriesCycle ? (
                  <>
                    This session belongs to <strong>{cycleName ?? 'this cycle'}</strong>
                    {isFrameLinked && trackId
                      ? <> · <strong>{trackName ?? 'this track'}</strong></>
                      : pendingTrackId && <> · <span className="text-[var(--text-ter)]">finding the track…</span></>}.
                  </>
                ) : (
                  <>The workflow allows only {SESSION_CONTEXT_MODE_LABELS[inferredContext]} sessions here, so this one is not tied to <strong>{cycleName ?? 'the cycle'}</strong>.</>
                )}
              </p>
              <button
                type="button"
                onClick={() => setChangingFrame(true)}
                className="shrink-0 text-xs text-[var(--text-sec)] underline underline-offset-2 hover:text-[var(--text-pri)]"
              >
                Change
              </button>
            </div>
            {carriesCycle && trackId && !isFrameLinked && (
              <p className="text-xs text-[var(--text-ter)]">
                The workflow does not allow {SESSION_CONTEXT_MODE_LABELS.cycle_linked} sessions here, so {trackName ?? 'the track'}’s
                people and items are not brought in.
              </p>
            )}
            {carriesCycle && !trackId && tracks.length > 1 && allowedModes.includes('cycle_linked') && (
              <div className="max-w-sm">
                <label className="mb-1.5 block text-xs font-medium text-[var(--text-sec)]">
                  Track <span className="font-normal text-[var(--text-ter)]">(optional — brings in its people and items)</span>
                </label>
                <select
                  value=""
                  onChange={(e) => setTrackId(e.target.value)}
                  className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:outline-none focus:ring-1 focus:ring-[var(--rule-bright)]"
                >
                  <option value="">— No track —</option>
                  {tracks.map((t) => (
                    <option key={t.id} value={t.id}>{t.name ?? t.id}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        ) : (
        <>
        <SessionModeChooser
          value={inferredContext}
          onChange={(mode) => {
            setChosenMode(mode);
            if (mode === 'isolated') {
              setCycleId('');
              setTrackId('');
              setTracks([]);
            }
          }}
          allowed={allowedModes}
          hasCycleAndTrack={Boolean(cycleId && trackId)}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              <MapPin size={11} className="inline mr-1" />Cycle
            </label>
            <select
              value={cycleId}
              onChange={(e) => {
                setScratchMode(false);
                setCycleId(e.target.value);
              }}
              disabled={scratchMode}
              className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-100 dark:focus:ring-slate-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <option value="">No cycle — workspace only</option>
              {activeCycles.map((c) => (
                <option key={c.id} value={c.id}>{c.name ?? c.id}</option>
              ))}
            </select>
          </div>
          {cycleId && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <MapPin size={11} className="inline mr-1" />Track
              </label>
              {tracks.length === 0 ? (
                <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] pt-2">No tracks in this cycle yet.</p>
              ) : (
                <select
                  value={trackId}
                  onChange={(e) => {
                    setScratchMode(false);
                    setTrackId(e.target.value);
                  }}
                  className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-100 dark:focus:ring-slate-blue-700"
                >
                  <option value="">— Pick a track —</option>
                  {tracks.map((t) => (
                    <option key={t.id} value={t.id}>{t.name ?? t.id}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
        </>
        )}

        {scratchMode && !frameSettled && (
          <p className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] px-3 py-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Scratch sessions are isolated. Platform item and collection sources will be disabled in S2; type, paste, and pull workarounds stay available.
          </p>
        )}

        {/* Track inheritance preview — shown as soon as a track is selected */}
        {isFrameLinked && cycleName && trackName && (
          <div className="border border-alpine-green-200 dark:border-alpine-green-800 bg-alpine-green-50 dark:bg-alpine-green-900/20 px-4 py-3 space-y-2">
            <p className="text-xs font-semibold text-alpine-green-800 dark:text-alpine-green-300 flex items-center gap-1.5">
              <CheckCircle2 size={13} /> What will be inherited from <strong>{trackName}</strong>
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              <span className="flex items-center gap-1.5">
                <Users size={12} className="text-alpine-green-600 dark:text-alpine-green-400 shrink-0" />
                {frameStats.loading ? '…' : frameStats.participantCount === 0 ? 'No participants yet' : `${frameStats.participantCount} participant${frameStats.participantCount !== 1 ? 's' : ''}`}
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">→ S1 pre-populated</span>
              </span>
              <span className="flex items-center gap-1.5">
                <Package size={12} className="text-alpine-green-600 dark:text-alpine-green-400 shrink-0" />
                {frameStats.loading ? '…' : frameStats.itemCount === 0 ? 'No item pool yet' : `${frameStats.itemCount} item${frameStats.itemCount !== 1 ? 's' : ''} in pool`}
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">→ S2 pre-populated</span>
              </span>
              <span className="flex items-center gap-1.5">
                <SlidersHorizontal size={12} className="text-alpine-green-600 dark:text-alpine-green-400 shrink-0" />
                {frameStats.loading ? '…' : frameStats.criteriaCount === 0 ? 'No criteria yet' : `${frameStats.criteriaCount} criteri${frameStats.criteriaCount === 1 ? 'on' : 'a'}`}
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">→ S3 preview</span>
              </span>
            </div>
            {(frameStats.participantCount === 0 || frameStats.itemCount === 0 || frameStats.criteriaCount === 0) && !frameStats.loading && (
              <p className="text-[11px] text-[var(--mustard)] dark:text-[var(--mustard)]">
                {frameStats.participantCount === 0 && 'Add participants to this track to have S1 auto-filled. '}
                {frameStats.itemCount === 0 && 'Add items to the track\'s pool to have S2 auto-filled.'}
                {frameStats.criteriaCount === 0 && ' Add criteria to preview S3 inheritance.'}
              </p>
            )}
          </div>
        )}

        {cycleId && (
          <ExchangeContextPanel cycleId={cycleId} />
        )}
      </section>

      {/* ── The room: four optional questions that shape the method recommendation ── */}
      <section className="space-y-3" data-testid="engagement-situation">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Read the room <span className="font-normal normal-case tracking-normal text-[var(--text-ter)]">(optional)</span>
          </h2>
          <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            What you know about this group changes which method fits. Answers are shown to every participant and
            describe the process, not the people. Skip any you are not sure of.
          </p>
        </div>
        <SituationQuestions value={situation} onChange={setSituation} disabled={creating} />
      </section>

      {/* ── Step 3: What is the main goal? ── */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] dark:text-[var(--text-sec)]">What do you need from this event?</h2>
        <div className="grid gap-3 sm:grid-cols-5">
          {STEP_ORDER.map((step) => {
            const isSelected = primaryStep === step;
            const isInherited = isFrameLinked && inheritedSteps.includes(step);
            return (
              <button
                key={step}
                onClick={() => setPrimaryStep(isSelected ? null : step)}
                className={[
                  'flex flex-col gap-1.5  border p-3 text-left transition',
                  isSelected
                    ? 'border-slate-blue-500 bg-slate-blue-50 ring-1 ring-slate-blue-200 dark:bg-slate-blue-900/30 dark:ring-slate-blue-700'
                    : 'border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-slate-blue-400 hover:shadow-sm',
                ].join(' ')}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[11px] font-bold uppercase tracking-widest ${isSelected ? 'text-slate-blue-700 dark:text-slate-blue-300' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
                    {step}
                  </span>
                  {isInherited && (
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-alpine-green-800 bg-alpine-green-100 dark:text-alpine-green-400 dark:bg-alpine-green-900/30 rounded px-1 py-0.5">
                      Track
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{STEP_META[step].label}</span>
                <span className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-tight">{STEP_META[step].blurb}</span>
                <span className="mt-1 text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {requiredForGoal(step).length > 0
                    ? `Needs: ${requiredForGoal(step).join(' + ')}`
                    : 'Needs nothing else'}
                </span>
              </button>
            );
          })}
        </div>

        {/* Prerequisites callout + launch */}
        {primaryStep && (
          <div className="border border-slate-blue-200 dark:border-slate-blue-700 bg-slate-blue-50 dark:bg-slate-blue-900/20 px-4 py-3 text-sm space-y-2">
            <p className="font-medium text-slate-blue-900 dark:text-slate-blue-100">
              Goal: <strong>{primaryStep} — {STEP_META[primaryStep].label}</strong>
            </p>
            <div className="flex flex-wrap gap-2">
              {requiredSteps.length > 0 ? (
                <>
                  <span className="text-xs text-slate-blue-700 dark:text-slate-blue-300 self-center">Needs:</span>
                  {requiredSteps.map((dep) => {
                    const inherited = isFrameLinked && inheritedSteps.includes(dep);
                    return (
                      <span
                        key={dep}
                        className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${inherited ? 'bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-200' : 'bg-slate-blue-100 text-slate-blue-800 dark:bg-slate-blue-900/40 dark:text-slate-blue-200'}`}
                      >
                        <CheckCircle2 size={10} />
                        {dep} — {STEP_META[dep].label}
                        {inherited && <span className="text-[9px] opacity-70 ml-0.5">(from track)</span>}
                      </span>
                    );
                  })}
                </>
              ) : (
                <span className="text-xs text-slate-blue-700 dark:text-slate-blue-300">This step needs nothing else to run.</span>
              )}
            </div>
            {optionalSteps.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-slate-blue-700/80 dark:text-slate-blue-300/80 self-center">Optional — skip any:</span>
                {optionalSteps.map((dep) => {
                  const inherited = isFrameLinked && inheritedSteps.includes(dep);
                  return (
                    <span
                      key={dep}
                      className="inline-flex items-center gap-1 rounded border border-slate-blue-200 dark:border-slate-blue-700/60 px-2 py-0.5 text-[11px] font-medium text-slate-blue-700 dark:text-slate-blue-300"
                    >
                      {dep} — {STEP_META[dep].label}
                      {inherited && <span className="text-[9px] opacity-70 ml-0.5">(from track)</span>}
                    </span>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] leading-snug text-slate-blue-700/90 dark:text-slate-blue-300/90">
              Every step can be declared on its own. A step can only be run collaboratively when participants
              (S1) are on the roster — without them there is nobody to collaborate with.
              {GOAL_CONDITIONAL_NOTE[primaryStep] ? ` ${GOAL_CONDITIONAL_NOTE[primaryStep]}` : ''}
            </p>
            <button
              onClick={() => void startFromGoal(primaryStep)}
              disabled={creating}
              className="mt-1 flex items-center gap-1.5 bg-slate-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-blue-700 disabled:opacity-60"
            >
              {creating ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
              {creating ? 'Creating…' : `Start ${primaryStep} event`}
            </button>
          </div>
        )}
      </section>

      {/* ── Secondary: start from a common situation (collapsed) ── */}
      <section className="space-y-3">
        <button
          type="button"
          onClick={() => setShowJobs((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
        >
          {showJobs ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Or start from a common situation
        </button>
        <div className={showJobs ? 'grid gap-3 sm:grid-cols-2' : 'hidden'}>
          {SESSION_JOBS.map((job) => (
            <button
              key={job.id}
              type="button"
              onClick={() => void startFromJob(job)}
              disabled={creating}
              className="group flex h-full flex-col border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-4 text-left transition hover:border-slate-blue-500 hover:bg-slate-blue-50 dark:hover:bg-slate-blue-900/20 disabled:cursor-wait disabled:opacity-60"
            >
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] group-hover:text-slate-blue-700 dark:group-hover:text-slate-blue-200">{job.title}</span>
              <span className="mt-1 flex-1 text-xs leading-relaxed text-[var(--text-ter)] dark:text-[var(--text-ter)]">{job.description}</span>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-blue-600 dark:text-slate-blue-400">
                {creating ? 'Creating…' : 'Start with this job'}
                {!creating && <ArrowRight size={12} />}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Custom start ── */}
      <section className="space-y-3 border border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)] p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-sec)] dark:text-[var(--text-sec)]">…or start with a custom name</h2>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Event name (optional)"
          disabled={creating}
          className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-100 dark:focus:ring-slate-blue-700 disabled:opacity-60"
        />
        <button
          onClick={() => void startCustom()}
          disabled={creating}
          className="flex items-center gap-1.5 bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-4 py-2 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-60"
        >
          {creating ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
          {creating ? 'Creating…' : 'Create event'}
        </button>
      </section>
    </div>
  );
}
